import IntlMessages from 'helpers/IntlMessages';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Modal, Spinner } from 'react-bootstrap';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { UserContext } from 'contexts/user/UserContext';
import { clearPendingChatInvite } from 'pages/Auth/postLoginRedirect';
import { getDiscourseActivationUrl } from 'services/discourseActivation';

type Preview = {
  inviterName: string;
  chatName: string;
  name: string;
  kind: 'community' | 'commons' | 'ecoregion' | 'activity';
  accessKind: 'public' | 'members' | 'team';
  role: 'facilitator' | 'teacher' | 'mentor' | null;
};

class InvalidInvitationError extends Error {}

const ChatInvitation = (): React.ReactElement => {
  const { token } = useParams<{ token: string }>();
  const history = useHistory();
  const location = useLocation();
  const { isAuthenticated } = useContext(UserContext);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewToken, setPreviewToken] = useState<string | null>(null);
  const [phase, setPhase] = useState<'preview' | 'waiting'>(() =>
    new URLSearchParams(location.search).get('phase') === 'wait' ? 'waiting' : 'preview');
  const [error, setError] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [previewRetry, setPreviewRetry] = useState(0);
  const [acceptErrorId, setAcceptErrorId] = useState('chat.invite-error');
  const [accepting, setAccepting] = useState(false);
  const [slow, setSlow] = useState(false);
  const [needsActivation, setNeedsActivation] = useState(false);
  const [retry, setRetry] = useState(0);
  const statusUrl = useMemo(() => `/api/discourse/chat-invitations/${encodeURIComponent(token)}/status`, [token]);
  const currentPreview = previewToken === token ? preview : null;

  useEffect(() => {
    let active = true;
    setPreview(null);
    setPreviewToken(null);
    setError(false);
    setUnavailable(false);
    fetch(`/api/discourse/chat-invitations/${encodeURIComponent(token)}`, { credentials: 'same-origin' })
      .then(async response => {
        if (response.status === 404 || response.status === 409) throw new InvalidInvitationError();
        if (!response.ok) throw new Error('Invitation temporarily unavailable');
        return response.json();
      })
      .then(data => {
        if (active) {
          setPreview(data);
          setPreviewToken(token);
          setError(false);
          if (data.accepted) setPhase('waiting');
        }
      })
      .catch(fetchError => {
        if (active) {
          if (fetchError instanceof InvalidInvitationError) {
            clearPendingChatInvite();
            setUnavailable(true);
          }
          setError(true);
        }
      });
    return () => { active = false; };
  }, [token, previewRetry]);

  const accept = async () => {
    if (!currentPreview) return;
    if (!isAuthenticated) {
      history.push('/login', { from: `${location.pathname}${location.search}${location.hash}` });
      return;
    }
    setError(false);
    setAccepting(true);
    try {
      const response = await fetch(`/api/discourse/chat-invitations/${encodeURIComponent(token)}/accept`, {
        method: 'POST', credentials: 'same-origin',
      });
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        setAcceptErrorId(data?.error === 'Verify your ARK email before accepting this invitation'
          ? 'chat.invite-verify-email' : 'chat.invite-error');
        throw new Error('Could not accept invitation');
      }
      history.replace(`${location.pathname}?phase=wait`);
      setPhase('waiting');
    } catch {
      setError(true);
    } finally {
      setAccepting(false);
    }
  };

  const activateDiscourse = useCallback(() => {
    const returnUrl = `${window.location.origin}/chat/invite/${encodeURIComponent(token)}?phase=wait&sso=done`;
    window.location.assign(getDiscourseActivationUrl(returnUrl));
  }, [token]);

  useEffect(() => {
    if (phase !== 'waiting') return;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let checks = 0;
    const poll = async () => {
      try {
        const response = await fetch(statusUrl, { credentials: 'same-origin' });
        if (response.status === 404 || response.status === 409) throw new InvalidInvitationError();
        if (!response.ok) throw new Error('Chat status temporarily unavailable');
        const status = await response.json();
        if (!active) return;
        if (status.state === 'ready' && status.chatUrl) {
          clearPendingChatInvite();
          window.location.assign(status.chatUrl);
          return;
        }
        if (status.state === 'needs_acceptance') {
          setPhase('preview');
          return;
        }
        if (status.state === 'needs_activation') {
          if (new URLSearchParams(location.search).get('sso') !== 'done') {
            activateDiscourse();
          } else {
            setNeedsActivation(true);
          }
          return;
        }
        checks += 1;
        if (checks >= 20) setSlow(true);
        timer = setTimeout(poll, checks >= 20 ? 5000 : 3000);
      } catch (statusError) {
        if (active) {
          if (statusError instanceof InvalidInvitationError) clearPendingChatInvite();
          setError(true);
        }
      }
    };
    void poll();
    return () => { active = false; if (timer) clearTimeout(timer); };
  }, [phase, retry, statusUrl, location.search, activateDiscourse]);

  const leave = () => {
    clearPendingChatInvite();
    history.push('/');
  };

  const accessId = currentPreview?.accessKind === 'team'
    ? currentPreview.role === 'teacher' ? 'chat.invite-accept-teacher'
      : currentPreview.role === 'facilitator' ? 'chat.invite-accept-facilitator' : 'chat.invite-accept-mentor'
    : currentPreview?.accessKind === 'members' ? 'chat.invite-accept-member' : 'chat.invite-accept-public';

  return <main className="container py-4">
    <Modal className="chat-invitation-modal" show={phase === 'preview' && !!currentPreview} onHide={leave} centered>
      <Modal.Header closeButton><Modal.Title><IntlMessages id="chat.invite-modal-title" /></Modal.Title></Modal.Header>
      <Modal.Body className="text-center">
        <img src="/assets/img/stewards.png" alt="" aria-hidden="true"
          style={{ width: '100%', maxWidth: '12rem', height: 'auto', marginBottom: '1rem' }} />
        {currentPreview && <>
          <p><IntlMessages id="chat.invite-from" values={{ inviterName: currentPreview.inviterName }} /></p>
          <h2>{currentPreview.chatName.trim() === currentPreview.name.trim()
            ? currentPreview.name
            : <IntlMessages id="chat.invite-to-chat" values={{ chatName: currentPreview.chatName, name: currentPreview.name }} />}</h2>
          <Alert variant={currentPreview.accessKind === 'team' ? 'warning' : 'info'}>
            <IntlMessages id={accessId} values={{ name: currentPreview.name }} />
          </Alert>
        </>}
        {error && <Alert variant="danger"><IntlMessages id={acceptErrorId} /></Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={leave}><IntlMessages id="chat.invite-later" /></Button>
        <Button onClick={() => void accept()} disabled={accepting}>
          {accepting && <Spinner animation="border" size="sm" className="mr-2" />}
          <IntlMessages id={currentPreview?.accessKind === 'public' ? 'chat.invite-open-chat' : 'chat.invite-accept'} />
        </Button>
      </Modal.Footer>
    </Modal>
    {phase === 'waiting' && <Card className="p-4 text-center mx-auto" style={{ maxWidth: '40rem' }}>
      <img src="/assets/img/stewards.png" alt="" aria-hidden="true"
        style={{ width: '100%', maxWidth: '12rem', height: 'auto', margin: '0 auto 1rem' }} />
      <div><Spinner animation="border" role="status" className="mb-3" /></div>
      <h1><IntlMessages id="chat.invite-setting-up" /></h1>
      <p><IntlMessages id={currentPreview?.accessKind === 'public'
        ? 'chat.invite-preparing-public' : 'chat.invite-preparing-membership'}
        values={{ name: currentPreview?.name || '' }} /></p>
      {slow && <>
        <p><IntlMessages id="chat.invite-taking-longer" /></p>
        <Button variant="outline-primary" onClick={() => { setSlow(false); setRetry(value => value + 1); }}>
          <IntlMessages id="chat.invite-check-again" />
        </Button>
      </>}
      {needsActivation && <Button onClick={activateDiscourse}>
        <IntlMessages id="chat.invite-connect-discourse" />
      </Button>}
      {error && <>
        <Alert variant="danger"><IntlMessages id="chat.invite-status-error" /></Alert>
        <Button onClick={() => { setError(false); setRetry(value => value + 1); }}>
          <IntlMessages id="general.retry" />
        </Button>
      </>}
    </Card>}
    {phase === 'preview' && !currentPreview && !error && <div className="text-center py-4">
      <Spinner animation="border" role="status" /> <IntlMessages id="general.loading" />
    </div>}
    {phase === 'preview' && !currentPreview && error && <Alert variant="danger">
      <IntlMessages id={unavailable ? 'chat.invite-unavailable' : 'chat.invite-error'} />
    </Alert>}
    {phase === 'preview' && !currentPreview && error && !unavailable && <Button onClick={() => {
      setError(false);
      setPreviewRetry(value => value + 1);
    }}><IntlMessages id="general.retry" /></Button>}
  </main>;
};

export default ChatInvitation;
